import camelCase from 'lodash/camelCase';
import { useEffect } from 'react';

import { useAnalyticsContext } from './analytics-context.ts';

const getEventPayload = (element: HTMLElement) => {
  const actionSubject = element.getAttribute('data-gas-action-subject') ?? '';
  const action = element.getAttribute('data-gas-action') ?? '';
  const actionSubjectId = element.getAttribute('data-gas-action-subject-id') ?? undefined;
  const source = element.getAttribute('data-gas-source') ?? '';

  // Extract all analytics attributes, which are defined in the form
  // data-gas-attribute-<name>
  const attributes = Object.fromEntries(
    Object.values(element.attributes)
      .filter((key) => key.name.startsWith('data-gas-attribute-'))
      .map((key) => [key.name, element.getAttribute(key.name)] as const)
      .map(([key, value]) => [camelCase(key.replace(/^data-gas-attribute-/, '')), value]),
  );

  return {
    action,
    source,
    actionSubject,
    actionSubjectId,
    attributes,
  };
};

// The use of the ownerDocument.defaultView avoids globals and passes
// tests, see https://github.com/testing-library/user-event/issues/434#issuecomment-679274928.
const isInstanceOfInputElement = (
  element: HTMLElement,
  instanceToCheck?: HTMLElement,
) => {
  return (
    element.ownerDocument.defaultView &&
    (instanceToCheck ?? element) instanceof
      element.ownerDocument.defaultView.HTMLInputElement
  );
};

export const useAnalyticsListeners = () => {
  const { client: analyticsWebClient } = useAnalyticsContext();

  useEffect(() => {
    if (!analyticsWebClient) {
      return () => {};
    }

    const sendFormAnalyticsEvent = async (form: HTMLFormElement | HTMLInputElement) => {
      const rawEventPayload = getEventPayload(form);

      const eventPayload = {
        ...rawEventPayload,
        action: rawEventPayload.action || 'submitted',
        source: rawEventPayload.source || 'canvas',
      };

      try {
        await analyticsWebClient.sendUIEvent(eventPayload);
      } catch (e) {
        console.error(e);
      }
    };

    const sendUiAnalyticsEvent = async (form: HTMLElement) => {
      const eventPayload = getEventPayload(form);

      try {
        await analyticsWebClient.sendUIEvent(eventPayload);
      } catch (e) {
        console.error(e);
      }
    };

    const onSubmit = async (e: SubmitEvent) => {
      if (!(e.target instanceof HTMLFormElement)) {
        return;
      }
      const form = e.target.closest('form');

      if (
        form instanceof HTMLFormElement &&
        !!form.dataset.gasActionSubject &&
        !!form.dataset.gasActionSubjectId
      ) {
        sendFormAnalyticsEvent(e.target);
      }
    };

    const onChange = async (e: Event) => {
      const element = e.target as HTMLElement;
      if (!(element instanceof HTMLFormElement || isInstanceOfInputElement(element))) {
        return;
      }

      const form = element.closest('form');

      if (form instanceof HTMLFormElement && !!form.dataset.gasFireOnChange) {
        sendFormAnalyticsEvent(form);
        // Early return to prevent further conditional checks
        return;
      }

      const input = element.closest('input');
      if (
        input &&
        isInstanceOfInputElement(element, input) &&
        !!input.dataset.gasFireOnChange
      ) {
        sendFormAnalyticsEvent(input);
      }
    };

    const onClick = async (e: Event) => {
      if (!(e.target instanceof HTMLElement) && !(e.target instanceof SVGElement)) {
        return;
      }

      const element = e.target.closest('[data-gas-action]');

      if (element instanceof HTMLElement) {
        if (element.getAttribute('data-gas-ignore-capture') !== null) {
          return;
        }
        sendUiAnalyticsEvent(element);
      }
    };

    document.addEventListener('submit', onSubmit);
    document.addEventListener('change', onChange);
    document.addEventListener('click', onClick);

    return () => {
      document.removeEventListener('submit', onSubmit);
      document.removeEventListener('change', onChange);
      document.removeEventListener('click', onClick);
    };
  }, [analyticsWebClient]);
};
